import { ReactNode } from 'react';
import { TextStyle } from 'react-native';
import { createUseStyles } from 'react-jss';
import { colors } from '@packages/core/styles';
import { Icon, StsIconName, Typography } from '@packages/ui/shared';

const titleStyle: TextStyle = { fontSize: 19, textAlign: 'center' };

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 210,
        borderRadius: 4,
        flexDirection: 'column',
    },
    iconWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: colors.grayOne,
        borderRadius: '50%',
        width: 80,
        height: 80,
    },
});

export type RadioCardGroupEmptyStateProps = {
    ariaLabel: string;
    children?: ReactNode;
    iconName: StsIconName;
    title: string;
};

/**
 * The empty state for a `RadioCardGroup`, shown in place of its cards.
 *
 * Renders an icon, and title with `children` beneath them for whatever action the caller
 * wants to offer. Keeping the action a slot means the caller owns its label, its handler and its loading state, so the
 * empty state never has to know why it is being shown.
 *
 * @param props - The props for the `RadioCardGroupEmptyState` component.
 * @returns The rendered `RadioCardGroupEmptyState` component.
 */
export function RadioCardGroupEmptyState({ ariaLabel, children, iconName, title }: RadioCardGroupEmptyStateProps) {
    const classes = useStyles();

    return (
        <div aria-label={ariaLabel} className={classes.container} role="group">
            <div className={classes.iconWrapper}>
                <Icon color="grayFive" name={iconName} size={40} />
            </div>

            <Typography variant="h3" color="textDarkSecondary" style={titleStyle}>
                {title}
            </Typography>

            {children}
        </div>
    );
}
